import type React from 'react';
import type { ChangeEvent } from 'react';
import { useCallback, forwardRef, useRef } from 'react';
import type { InputProps, InputRef, TextAreaProps } from 'antd/lib/input';
import Input from 'antd/lib/input';
import type { TextAreaRef } from 'antd/lib/input/TextArea';
import { SInputGroup } from './Input.styles';

export type { TextAreaProps, TextAreaRef } from 'antd/lib/input/TextArea';
export type { InputProps, InputRef } from 'antd/lib/input';

type valueType = string;
type EventType = ChangeEvent<HTMLInputElement> & ChangeEvent<HTMLTextAreaElement>;
type AdvanceInputProps = {
  formatter: (value: valueType) => valueType;
  parser: (value: valueType) => valueType;
};

type UIInputProps = InputProps & React.RefAttributes<InputRef> & AdvanceInputProps;
type UITextAreaProps = TextAreaProps & React.RefAttributes<TextAreaRef> & AdvanceInputProps;

const withInputHoc = <T extends UIInputProps | UITextAreaProps>(
  InputField: React.ComponentType<T>,
) => {
  return forwardRef((props: T, ref) => {
    const {
      onChange,
      value,
      size = 'middle',
      formatter = (val: valueType): valueType => val,
      parser = (val: valueType): valueType => val,
      ...rest
    } = props;

    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleChange = useCallback(
      (event: EventType) => {
        const inputElement = event.target;
        const { selectionStart } = inputElement;

        const formattedValue = formatter(inputElement.value);

        if (onChange) {
          onChange({
            ...event,
            target: {
              ...event.target,
              value: formattedValue,
            },
          });
        }

        if (inputElement && inputRef.current) {
          inputRef.current.setSelectionRange(selectionStart, selectionStart);
        }
      },
      [formatter, onChange],
    );

    return (
      <InputField
        onChange={handleChange}
        value={parser(value as valueType)}
        size={size}
        ref={(el: any) => {
          inputRef.current = el;
          if (typeof ref === 'function') {
            ref(el);
          } else if (ref) {
            ref.current = el;
          }
        }}
        {...(rest as T)}
      />
    );
  });
};

const UIInputTextArea = withInputHoc<UITextAreaProps>(Input.TextArea);
const UIInput: any = withInputHoc<UIInputProps>(Input);

UIInput.Group = SInputGroup;
UIInput.Password = Input.Password;
UIInput.Search = Input.Search;
UIInput.TextArea = UIInputTextArea;

export { UIInput as Input };
